import { toRefs, reactive } from 'vue';
import Api from '../api/BaseRequest';

const initialState = {
    // Basic wizard data
    hash: '',
    officeId: '',
    officeName: '',
    welcomeText: '',
    classes: [],
    maxLocalPhoneLength: 15,
    initialized: false,
    
    // Vipps user (the person using the wizard)
    vippsUser: {
        mobile: '',
        firstName: '',
        lastName: '',
        birthdate: '',
        email: '',
        exists: false,
        vippsId: ''
    },
    
    // Student data
    student: {
        mobile: '',
        firstName: '',
        lastName: '',
        birthdate: '',
        email: '',
        classId: '',
        comment: ''
    },
    
    // Contacts array
    contacts: [],
    
    // Wizard state
    userType: '', // '0' = register as student, '2' = register as contact
    currentStep: 1,
    activeAccordion: 1,
    loading: false,
    feedback: '',
    
    // Step status
    steps: {
        1: { completed: false, enabled: true },
        2: { completed: false, enabled: false },
        3: { completed: false, enabled: false },
        4: { completed: false, enabled: false }
    }
};

const state = reactive({ ...initialState });

export default {
    state: toRefs(state),
    
    // Step helpers
    isStepEnabled(step) {
        return state.steps[step]?.enabled || false;
    },
    
    isStepCompleted(step) {
        return state.steps[step]?.completed || false;
    },
    
    // Initialize from API
    async initializeFromAPI(hash) {
        try {
            state.loading = true;
            state.hash = hash;
            
            const data = await Api.Get(`/api/vipps-wizard-init`, { hash: encodeURIComponent(hash) });
            if (!data.success) {
                this.showError(data.message || 'Noe gikk galt. Vennligst prøv igjen senere.');
                return;
            }
            
            // Set office data
            state.officeName = data.data.office_name;
            state.officeId = data.data.office_id;
            state.classes = data.data.classes;
            state.welcomeText = data.data.welcome_text;
            state.maxLocalPhoneLength = data.data.maxLocalPhoneLength;
            
            // Set Vipps user if logged in
            if (data.data.vippsUserDetail) {
                const vipps = data.data.vippsUserDetail;
                state.vippsUser = {
                    mobile: vipps.mobile,
                    firstName: vipps.first_name,
                    lastName: vipps.last_name,
                    birthdate: vipps.birthdate,
                    email: vipps.email,
                    exists: vipps.user_exists,
                    vippsId: vipps.vipps_user_detail_id
                };
                
                this.enableStep2();
            }
            
            state.initialized = true;
        } catch (error) {
            console.error('Failed to initialize wizard:', error);
            this.showError('Noe gikk galt. Vennligst prøv igjen senere.');
        } finally {
            state.loading = false;
        }
    },

    // Step progression
    enableStep2() {
        state.steps[1].completed = true;
        state.steps[2].enabled = true;
        state.steps = {...state.steps}
        state.currentStep = 2;
        state.activeAccordion = 2;
        
        // Auto-select if user already exists and enable step 3 immediately
        if (state.vippsUser.exists) {
            state.userType = '2';
            this.enableStep3();
        }
    },

    enableStep3() {
        state.steps[2].completed = true;
        state.steps[3].enabled = true;
        state.steps = {...state.steps}
        state.currentStep = 3;
        state.activeAccordion = 3;
        this.setupFormData();
    },

    enableStep4() {
        state.steps[3].completed = true;
        // Enable and complete step 4
        state.steps[4].enabled = true;
        state.steps[4].completed = true;
        state.steps = {...state.steps}
        state.currentStep = 4;
        state.showOnlyStep4 = true;
        state.activeAccordion = 4;
    },

    toggleStep(step) {
        state.activeAccordion = (state.activeAccordion === step) ? 0 : step;
    },

    // Form setup based on user type
    setupFormData() {
        if (state.userType === '0') {
            // Register as student - populate student fields from Vipps
            state.student = {
                mobile: state.vippsUser.mobile,
                firstName: state.vippsUser.firstName,
                lastName: state.vippsUser.lastName,
                birthdate: state.vippsUser.birthdate,
                email: state.vippsUser.email,
                classId: '',
                comment: ''
            };
            
            // Clear any existing contacts when registering as student
            state.contacts = [];
        } else {
            // Register as contact - clear student, add Vipps as first contact
            state.student = {
                mobile: '',
                firstName: '',
                lastName: '',
                birthdate: '',
                email: '',
                classId: '',
                comment: ''
            };
            this.addVippsAsContact(true);
        }
    },

    // Contact management
    addVippsAsContact(isMainContact = false) {
        // Don't add if already exists
        if (state.contacts.find(c => c.isVipps)) return;
        
        const contact = {
            id: Date.now(),
            mobile: state.vippsUser.mobile,
            firstName: state.vippsUser.firstName,
            lastName: state.vippsUser.lastName,
            birthdate: state.vippsUser.birthdate,
            email: state.vippsUser.email,
            relation: '',
            isVipps: true,
            isRequired: isMainContact,
            isReadonly: isMainContact
        };
        
        if (isMainContact) {
            state.contacts.unshift(contact);
        } else {
            state.contacts.push(contact);
        }
    },

    addContact() {
        state.contacts.push({
            id: Date.now(),
            mobile: '',
            firstName: '',
            lastName: '',
            birthdate: '',
            email: '',
            relation: '',
            isVipps: false,
            isRequired: false,
            isReadonly: false
        });
    },

    removeContact(contactId) {
        const index = state.contacts.findIndex(c => c.id === contactId);
        if (index === -1) return;
        
        const contact = state.contacts[index];
        if (contact.isVipps || contact.isRequired) return;
        
        state.contacts.splice(index, 1);
    },

    updateContact(contactId, field, value) {
        const contact = state.contacts.find(c => c.id === contactId);
        if (contact) {
            // Allow editing of relation and email even for readonly contacts
            if (field === 'relation' || field === 'email' || !contact.isReadonly) {
                contact[field] = value;
            }
        }
    },

    // Check if student needs contact (under 18)
    checkContactRequirement() {
        if (!state.student.birthdate) return;
        
        try {
            const birthDate = new Date(state.student.birthdate.split('.').reverse().join('-'));
            const age = Math.floor((Date.now() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
            
            if (age < 18 && state.contacts.length === 0) {
                this.addContact();
                state.contacts[0].isRequired = true;
            }
        } catch (error) {
            console.error('Error parsing birthdate:', error);
        }
    },

    // User type selection
    selectUserType(type) {
        state.userType = type;
        this.clearFeedback(); // Feedback will probably not make sense after changing user type
        this.enableStep3();
    },

    // Data collection for API
    collectFormData() {
        const createdBy = state.userType === '0' ? 'student' : 'contact';
        
        return {
            office_id: state.officeId,
            created_by: createdBy,
            vipps_user_id: state.vippsUser.vippsId,
            
            // Student data
            student: {
                mobile: state.student.mobile,
                first_name: state.student.firstName,
                last_name: state.student.lastName,
                birthdate: state.student.birthdate,
                email: state.student.email,
                class_id: state.student.classId,
                comments: state.student.comment
            },
            
            // Contacts data
            contacts: state.contacts.map(contact => ({
                mobile: contact.mobile,
                first_name: contact.firstName,
                last_name: contact.lastName,
                birthdate: contact.birthdate,
                email: contact.email,
                relation: contact.relation,
                is_vipps_user: contact.isVipps
            }))
        };
    },

    // Registration
    async register() {
        if (state.loading) return;
        
        try {
            state.loading = true;
            this.clearFeedback();
            
            const data = this.collectFormData();
            const response = await Api.Post('/register_student_with_contact', data);

            if (response.success) {
                this.enableStep4();
            } else if (response.require_contact) {
                this.checkContactRequirement();
            } else {
                this.showError(response.message || 'Noe gikk galt. Vennligst prøv igjen senere.');
            }
        } catch (error) {
            console.error('Registration failed:', error);
            this.showError('Noe gikk galt. Vennligst prøv igjen senere.');
        } finally {
            state.loading = false;
        }
    },

    // Feedback
    setFeedback(content) {
        state.feedback = content;
    },

    showError(message) {
        this.setFeedback(`<p class="alert alert-danger">${message}</p>`);
    },

    clearFeedback() {
        state.feedback = '';
    }
};
